import * as React from "react";
import { Check, File as FileIcon, X } from "lucide-react";
import * as Sentry from "@sentry/react";
import { TableCell, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { FileWithProgress } from "@/views/drive/modals/uploadModal/types";
import { UploadConfirmCancel } from "@/views/drive/modals/uploadConfirmCancel";
import { useRemoveFileMutation } from "@/api/files";

interface Props {
  fileWithProgress: FileWithProgress;
  isUploading: boolean;
  onFileRemove: (file: FileWithProgress) => void;
}

export const UploadModalFilesRow: React.FC<Props> = ({ fileWithProgress, isUploading, onFileRemove }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [removeFile] = useRemoveFileMutation();
  const handleCancelUploading = () => {
    setIsOpen(true);
  };

  const handleOnCancel = () => {
    setIsOpen(false);
  };

  const handleAccept = async () => {
    if (fileWithProgress.fse_id) {
      try {
        await removeFile(fileWithProgress.fse_id).unwrap();
      } catch (error) {
        Sentry.captureException(error);
      }
    }
    onFileRemove(fileWithProgress);
  };

  return (
    <TableRow className={fileWithProgress.error ? "bg-red-100" : ""}>
      <TableCell className="font-medium">
        {isOpen && (
          <UploadConfirmCancel onCancel={handleOnCancel} fileWithProgress={fileWithProgress} onAccept={handleAccept} />
        )}
        <div className="flex items-center">
          <FileIcon className="mr-2 h-4 w-4" />
          {fileWithProgress.file.name}
        </div>
      </TableCell>
      <TableCell>{(fileWithProgress.file.size / 1024 / 1024).toFixed(2)} MB</TableCell>
      <TableCell>{fileWithProgress.file.type || "Unknown"}</TableCell>
      <TableCell>
        <Badge variant={fileWithProgress.status === "completed" ? "secondary" : "default"}>
          {fileWithProgress.status === "completed" ? <Check className="mr-1 h-4 w-4" /> : null}
          {fileWithProgress.status.charAt(0).toUpperCase() + fileWithProgress.status.slice(1)}
        </Badge>
      </TableCell>
      <TableCell>
        <Progress
          value={fileWithProgress.progress}
          className="w-[100px]"
          style={
            {
              "--progress-background":
                fileWithProgress.status === "started" || fileWithProgress.status === "uploading" ? "green" : undefined,
            } as React.CSSProperties
          }
        />
        <span className="ml-2 text-xs">{fileWithProgress.progress.toFixed(0)}%</span>
      </TableCell>
      <TableCell>
        <Button variant="ghost" size="sm" onClick={handleCancelUploading} disabled={isUploading}>
          <X className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
};
